import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Delta, EmptyState, HalalBadge } from "@/components/common";
import { timeAgo } from "@/lib/format";
import { Newspaper } from "lucide-react";

export const NewsFeed = ({ market, region, title = "Market News", limit = 20, height = "560px" }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const params = new URLSearchParams();
        if (market) params.set("market", market);
        if (region) params.set("region", region);
        params.set("limit", limit);
        const { data } = await api.get(`/market/news-feed?${params.toString()}`);
        if (active) setItems(data);
      } catch (e) {} finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [market, region, limit]);

  return (
    <Card className="p-4 shadow-premium" data-testid="news-feed">
      <div className="flex items-center gap-2 text-sm font-medium mb-3"><Newspaper className="h-4 w-4 text-[color:var(--tharwy-lime)]" />{title}</div>
      {loading ? <EmptyState title="Loading news..." /> : items.length === 0 ? (
        <EmptyState title="No news" hint="Company headlines will appear here" />
      ) : (
        <div className="divide-y overflow-auto" style={{ maxHeight: height }}>
          {items.map((n, i) => (
            <button key={`${n.symbol}-${n.headline}`} onClick={() => navigate(`/stock/${encodeURIComponent(n.symbol)}`)}
              className="w-full text-start py-3 hover:bg-accent/60 transition-colors rounded-md px-1" data-testid={`news-item-${n.symbol}`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary" className="font-medium">{n.symbol}</Badge>
                <span className="text-xs text-muted-foreground">{n.market}</span>
                {n.halal && <HalalBadge />}
                <Delta value={n.change_pct} className="text-xs ms-auto" />
              </div>
              <div className="text-sm font-medium leading-snug">{n.headline}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{n.company} · {n.category} · {timeAgo(n.time)}</div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};
